import { CircularProgress, Typography } from '@mui/material'
import { SmartDialog } from '../utilities/SmartDialog'
import Checklist from './CheckList'
import { getWorkflowStatus, setOpenJobsOnLoad } from './ChecklistPart'
import { apiGet } from '../Api_Functions'
import { useEffect, useState } from 'react'
import { useSmartInterval } from '../hooks/useSmartInterval'
import { useSmartTranslation } from '../hooks/useSmartTranslation'

const ChecklistDialog = ({ open, onClose, onAccept, fixtureUse }) => {
  const [workflows, setWorkflows] = useState(null)
  const { t_ } = useSmartTranslation()
  const [error, setError] = useState(null)

  useEffect(() => {
    // When opening the dialog reset all data and force a load
    if (open) {
      setWorkflows(null)
      setError(null)
    }
  }, [open])

  const parseChecklist = checkListData => {
    if (checkListData === null || checkListData === undefined) return []
    if (checkListData.parts === null || checkListData.parts === undefined) return []
    const parsedWorkflows = []
    for (const [partId, part] of Object.entries(checkListData.parts)) {
      const workflow = checkListData.workflows[part.workflowId]
      parsedWorkflows.push({
        workflowId: part.workflowId,
        partId,
        workflow: workflow.name,
        part: part.code,
        total: 1,
        jobs: workflow.jobs.map(jobId => {
          const job = checkListData.jobs[jobId]
          const jobDevices = part.jobDevices[jobId].map(deviceId => checkListData.devices[deviceId])
          const jobOperator = jobDevices.some(device => device.operatorType)
          const pendingTasks = part.pendingTasks
          const jobTasks = job.tasks.filter(taskId => {
            if (!pendingTasks.includes(taskId)) {
              return false
            }
            return true
          }).map(taskId => {
            const task = checkListData.tasks[taskId]
            const taskTools = task.tools.map(taskId => checkListData.tools[taskId])
            const parsedTask = {
              id: taskId,
              order: task.order,
              program: task.programName,
              programOk: jobOperator || task.programName !== null || job.programNotRequired === true,
              tools: taskTools.map(taskTool => {
                return {
                  id: taskTool.id,
                  name: taskTool.name,
                  number: taskTool.number,
                  ok: part.toolTasks[taskId][taskTool.id]
                }
              })
            }
            return parsedTask
          })
          const deviceOk = jobDevices.some(device => device.ok)
          const fixtureOk = checkFixture(part.fixture, jobDevices)
          return {
            id: jobId,
            name: job.name,
            technology: checkListData.technologies[job.technologyId],
            devices: jobDevices,
            deviceOk,
            fixtureName: part.fixture?.name,
            fixtureOk,
            quantity: deviceOk && fixtureOk && jobTasks.every(jt => jt.programOk && jt.tools.every(jtTool => jtTool.ok)) ? 1 : 0,
            tasks: jobTasks
          }
        })
      })
    }
    return parsedWorkflows
  }

  const checkFixture = (fixture, jobDevices) => {
    if (fixture == null) {
      return false
    }

    if (fixture.compatibleDevices == null) {
      return false
    }

    if (!jobDevices || jobDevices.length === 0) {
      return false
    }

    let fixtureOk = false
    for (const jobDevice of jobDevices) {
      if (fixture.compatibleDevices.some(deviceId => deviceId === jobDevice.id)) {
        fixtureOk = true
        break
      }
    }
    return fixtureOk
  }

  useSmartInterval(async () => {
    if (open) {
      const checkListData = await apiGet('checklist/actual')
      const data = parseChecklist(checkListData)
      data.forEach(workflow => {
        setOpenJobsOnLoad(workflow)
      })
      return data
    }
  }, 1000, (data) => {
    if (!open) {
      return
    }

    // If workflow is empty allow checklist
    if (data.length === 0) {
      onAccept()
      return
    }
    if (workflows === null) {
      setWorkflows(data)
      return
    }
    // Merge user info to the new workflows
    data.forEach(workflow => {
      const oldWorkflow = workflows.find((item) => item.partId === workflow.partId)
      if (oldWorkflow === null) {
        // Init as new workflow
        setOpenJobsOnLoad(workflow)
      } else {
        // Merge data from previous
        if (workflow.jobs && oldWorkflow.jobs) {
          workflow.jobs.forEach(job => {
            const newJobTasks = job.tasks.sort((a, b) => a.order - b.order).map(task => task.id)
            const oldJob = oldWorkflow.jobs.find((item) => {
              const oldJobTasks = item.tasks.sort((a, b) => a.order - b.order).map(task => task.id)
              if (newJobTasks.length !== oldJobTasks.length) {
                return false
              }
              return newJobTasks.every((value, index) => value === oldJobTasks[index])
            })
            if (oldJob !== null) {
              job.deviceForce = oldJob.deviceForce
              job.fixtureForce = oldJob.fixtureForce
              job.tasksOpen = oldJob.tasksOpen
              job.tasks.forEach(newTask => {
                const oldTask = oldJob.tasks.find((item) => item.id === newTask.id)
                if (oldTask !== null) {
                  newTask.toolForce = oldTask.toolForce
                  newTask.programForce = oldTask.programForce
                }
              })
            }
          })
        }
      }
    })
    setWorkflows(data)
  })

  const onAcceptClick = () => {
    if (workflows === null) {
      // Not loaded yet
      return
    }
    // Check if all is okay
    const status = getWorkflowsStatus(workflows)
    if (status === 'OK' || status === 'WARNING') {
      onAccept()
      return
    }

    // Check if any program has a problem
    let programError = false
    workflows.forEach(workflow => {
      if (workflow.workflowId === null || !workflow.jobs) {
        return
      }
      workflow.jobs.forEach(job => {
        job.tasks.forEach(task => {
          if (!task.programOk && !task.programForce) {
            programError = true
          }
        })
      })
    })

    if (programError) {
      setError(t_('Some program is not associated to the job '))
    } else {
      setError(t_('CHECKLIST IS NOT APPROVED!'))
    }
  }

  return (
    <SmartDialog
      setOpen={open}
      cancelCallback={onClose}
      acceptCallback={onAcceptClick}
      title={{
        visible: false,
        render: <Typography style={{ fontSize: '25px' }}>{t_('CheckList')}</Typography>,
        icon: ''
      }}
      renderComponent={workflows === null ? <CircularProgress /> : <Checklist workflows={workflows} setWorkflows={setWorkflows} error={error} closeError={() => setError(null)} fixtureUse={fixtureUse} />}
    />
  )
}

const getWorkflowsStatus = (workflows) => {
  let warning = false
  let error = false

  workflows.forEach(workflow => {
    if (workflow.workflowId === null || !workflow.jobs) {
      error = true
      return
    }
    const workflowStatus = getWorkflowStatus(workflow)
    if (workflowStatus === 'ERROR') {
      error = true
    } else if (workflowStatus === 'WARNING') {
      warning = true
    }
  })

  if (error) {
    return 'ERROR'
  }
  if (warning) {
    return 'WARNING'
  }
  return 'OK'
}

export default ChecklistDialog
